import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { DEFAULT_GROVE_ICON, isGroveIcon } from '@goaltree/backend/convex/lib/groveIcons'
import { isValidSlug, normalizeSlug } from '@goaltree/backend/convex/lib/groveShowcase'
import type { OptimisticLocalStore } from 'convex/browser'
import { insertAtTop } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'

import {
  GROVE_DESCRIPTION_MAX,
  GROVE_MESSAGE_MAX,
  GROVE_NAME_MAX,
} from '@/components/groves/grove-utils'

type GroveDetail = NonNullable<FunctionReturnType<typeof api.groves.get>>
type MineRow = FunctionReturnType<typeof api.groves.listMine>[number]
type TodayRow = FunctionReturnType<typeof api.groves.todayStatus>[number]

const cleanText = (raw: string, max: number) => raw.replace(/\s+/g, ' ').trim().slice(0, max)

function patchGet(
  store: OptimisticLocalStore,
  groveId: Id<'groves'>,
  fn: (g: GroveDetail) => GroveDetail,
) {
  for (const { args, value } of store.getAllQueries(api.groves.get)) {
    if (value && value._id === groveId) store.setQuery(api.groves.get, args, fn(value))
  }
}

function patchMine(
  store: OptimisticLocalStore,
  groveId: Id<'groves'>,
  fn: (r: MineRow) => MineRow | null,
) {
  const cur = store.getQuery(api.groves.listMine, {})
  if (!cur) return
  store.setQuery(
    api.groves.listMine,
    {},
    cur.flatMap((r) => (r._id === groveId ? (fn(r) ?? []) : [r])),
  )
}

function patchToday(
  store: OptimisticLocalStore,
  groveId: Id<'groves'>,
  fn: (r: TodayRow) => TodayRow | null,
) {
  const cur = store.getQuery(api.groves.todayStatus, {})
  if (!cur) return
  store.setQuery(
    api.groves.todayStatus,
    {},
    cur.flatMap((r) => (r.groveId === groveId ? (fn(r) ?? []) : [r])),
  )
}

function adjustUnreadTotal(store: OptimisticLocalStore, delta: number) {
  const cur = store.getQuery(api.groves.unreadTotal, {})
  if (cur === undefined || !delta) return
  store.setQuery(api.groves.unreadTotal, {}, Math.max(0, cur + delta))
}

function mineRow(store: OptimisticLocalStore, groveId: Id<'groves'>) {
  return store.getQuery(api.groves.listMine, {})?.find((r) => r._id === groveId)
}

export function optimisticUpdateGrove(
  store: OptimisticLocalStore,
  args: { groveId: Id<'groves'>; name?: string; icon?: string; description?: string },
) {
  const name = args.name !== undefined ? cleanText(args.name, GROVE_NAME_MAX) : undefined
  if (name === '') return
  const icon =
    args.icon !== undefined ? (isGroveIcon(args.icon) ? args.icon : DEFAULT_GROVE_ICON) : undefined
  const patch = { ...(name !== undefined && { name }), ...(icon !== undefined && { icon }) }
  patchGet(store, args.groveId, (g) => ({
    ...g,
    ...patch,
    ...(args.description !== undefined && {
      description: cleanText(args.description, GROVE_DESCRIPTION_MAX) || null,
    }),
  }))
  patchMine(store, args.groveId, (r) => ({ ...r, ...patch }))
  patchToday(store, args.groveId, (r) => ({ ...r, ...patch }))
}

export function optimisticSetGroveMuted(
  store: OptimisticLocalStore,
  { groveId, muted }: { groveId: Id<'groves'>; muted: boolean },
) {
  const row = mineRow(store, groveId)
  if (row && row.muted !== muted) adjustUnreadTotal(store, muted ? -row.unread : row.unread)
  patchGet(store, groveId, (g) => ({ ...g, me: { ...g.me, muted } }))
  patchMine(store, groveId, (r) => ({ ...r, muted }))
  if (muted) patchToday(store, groveId, (r) => ({ ...r, unread: 0 }))
  else if (row) patchToday(store, groveId, (r) => ({ ...r, unread: row.unread }))
}

// Leave/delete: drop the grove from my lists; its detail page navigates away on success.
export function optimisticDropGrove(
  store: OptimisticLocalStore,
  { groveId }: { groveId: Id<'groves'> },
) {
  const row = mineRow(store, groveId)
  if (row && !row.muted) adjustUnreadTotal(store, -row.unread)
  patchMine(store, groveId, () => null)
  patchToday(store, groveId, () => null)
}

export function optimisticRemoveGroveMember(
  store: OptimisticLocalStore,
  { groveId, userId }: { groveId: Id<'groves'>; userId: string },
) {
  patchGet(store, groveId, (g) => {
    if (!g.members.some((m) => m.userId === userId)) return g
    const members = g.members.filter((m) => m.userId !== userId)
    return {
      ...g,
      members,
      memberCount: members.length,
      leaderboard: g.leaderboard
        .filter((m) => m.userId !== userId)
        .map((m, i) => ({ ...m, rank: i + 1 })),
      allInToday: members.length > 0 && members.every((m) => m.checkedInToday),
      checkedInToday: members.filter((m) => m.checkedInToday).length,
      weekTotal: members.reduce((s, m) => s + m.weekCount, 0),
    }
  })
  patchMine(store, groveId, (r) => ({ ...r, memberCount: Math.max(1, r.memberCount - 1) }))
}

export function optimisticMarkGroveRead(
  store: OptimisticLocalStore,
  { groveId }: { groveId: Id<'groves'> },
) {
  const row = mineRow(store, groveId)
  if (row && !row.muted) adjustUnreadTotal(store, -row.unread)
  patchMine(store, groveId, (r) => (r.unread ? { ...r, unread: 0 } : r))
  patchToday(store, groveId, (r) => (r.unread ? { ...r, unread: 0 } : r))
}

export function optimisticDeleteGroveMessage(
  store: OptimisticLocalStore,
  { messageId }: { messageId: Id<'groveMessages'> },
) {
  for (const { args, value } of store.getAllQueries(api.groves.listMessages)) {
    if (!value?.page.some((m) => m._id === messageId)) continue
    store.setQuery(api.groves.listMessages, args, {
      ...value,
      page: value.page.map((m) => (m._id === messageId ? { ...m, body: '', deleted: true } : m)),
    })
  }
}

export function optimisticSendGroveMessage(
  store: OptimisticLocalStore,
  { groveId, body }: { groveId: Id<'groves'>; body: string },
) {
  const text = body.trim().slice(0, GROVE_MESSAGE_MAX)
  if (!text) return
  let me: GroveDetail['members'][number] | undefined
  for (const { value } of store.getAllQueries(api.groves.get)) {
    if (value?._id === groveId) me = value.members.find((m) => m.isMe)
  }
  const now = Date.now()
  insertAtTop({
    paginatedQuery: api.groves.listMessages,
    argsToMatch: { groveId },
    localQueryStore: store,
    item: {
      _id: `optimistic-${now}` as Id<'groveMessages'>,
      kind: 'text',
      body: text,
      deleted: false,
      createdAt: now,
      userId: me?.userId ?? null,
      isMine: true,
      author: me ? { name: me.name, image: me.image, handle: me.handle } : null,
    },
  })
}

export function optimisticSetGroveShowcase(
  store: OptimisticLocalStore,
  { groveId, on, slug: raw }: { groveId: Id<'groves'>; on: boolean; slug?: string },
) {
  patchGet(store, groveId, (g) => {
    if (!g.showcase) return g
    const slug = raw === undefined ? g.showcase.slug : normalizeSlug(raw)
    if (!on && slug === g.showcase.slug) return { ...g, showcase: { ...g.showcase, on: false } }
    if (!slug || !isValidSlug(slug)) return g
    return { ...g, showcase: { on, slug } }
  })
}

export function optimisticDropJoinRequest(
  store: OptimisticLocalStore,
  { requestId }: { requestId: Id<'groveJoinRequests'> },
) {
  for (const { args, value } of store.getAllQueries(api.groveShowcase.listRequests)) {
    if (value?.some((r) => r._id === requestId))
      store.setQuery(
        api.groveShowcase.listRequests,
        args,
        value.filter((r) => r._id !== requestId),
      )
  }
}

export function optimisticRequestToJoin(store: OptimisticLocalStore, { slug }: { slug: string }) {
  const status = store.getQuery(api.groveShowcase.viewerStatus, { slug })
  const grove = store.getQuery(api.groveShowcase.getGroveShowcase, { slug })
  if (status?.status !== 'none' || !grove || grove.full) return
  store.setQuery(api.groveShowcase.viewerStatus, { slug }, { status: 'requested', groveId: null })
}
